/* Sessão: quem está logado, o que pode e quais empresas enxerga.
 *
 * O cookie é HttpOnly — o JavaScript não o lê, de propósito: assim um XSS não
 * consegue copiar a sessão. A consequência é que o front não sabe se está
 * autenticado até perguntar. Por isso a primeira coisa que a aplicação faz é
 * `GET /auth/me`, e existe um estado `verificando` antes de decidir entre a
 * tela de entrada e o painel. Sem ele, a entrada pisca a cada recarregamento
 * de quem já está logado.
 *
 * As permissões vêm do servidor. O front as usa para **esconder o que não
 * adianta mostrar**, não para autorizar: quem autoriza é a API, que recusa com
 * 403 mesmo se alguém chamar a rota direto.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { api, quandoExpirar } from '../api/cliente';

const Contexto = createContext(null);

export function ProvedorSessao({ children }) {
  const [usuario, definirUsuario] = useState(null);
  const [tenants, definirTenants] = useState([]);
  const [verificando, definirVerificando] = useState(true);

  const carregar = useCallback(async () => {
    try {
      const dados = await api.eu();
      definirUsuario(dados.usuario);
      definirTenants(dados.tenants || []);
    } catch {
      // 401 aqui é o caso normal de quem ainda não entrou.
      definirUsuario(null);
      definirTenants([]);
    } finally {
      definirVerificando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Sessão expirada no meio do uso: qualquer chamada que responda 401 cai aqui
  // e derruba o estado local, levando de volta à entrada.
  useEffect(() => {
    quandoExpirar(() => {
      definirUsuario(null);
      definirTenants([]);
    });
  }, []);

  const entrar = useCallback(async (email, senha) => {
    const dados = await api.entrar(email, senha);
    definirUsuario(dados.usuario);

    // O login devolve o perfil, não o escopo. Buscar os tenants agora evita
    // que a primeira tela apareça com o seletor de empresa vazio.
    const eu = await api.eu();
    definirTenants(eu.tenants || []);

    return dados.usuario;
  }, []);

  const sair = useCallback(async () => {
    try {
      await api.sair();
    } finally {
      // Mesmo que a revogação falhe, o estado local cai: a pessoa pediu para
      // sair, e deixá-la na tela logada seria pior do que a sessão sobreviver
      // no servidor até expirar.
      definirUsuario(null);
      definirTenants([]);
    }
  }, []);

  const valor = useMemo(() => {
    const permissoes = new Set(usuario?.permissoes || []);

    return {
      usuario,
      tenants,
      verificando,
      autenticado: Boolean(usuario),
      pode: (acao) => permissoes.has(acao),
      entrar,
      sair,
      recarregar: carregar,
    };
  }, [usuario, tenants, verificando, entrar, sair, carregar]);

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao() {
  const contexto = useContext(Contexto);
  if (!contexto) throw new Error('useSessao fora do ProvedorSessao');
  return contexto;
}
